import { useSettingsStore } from '../../lib/stores/settingsStore';
import { customerOrderSettings } from '../../lib/utils/customerOrderSettings';
import { coreCall } from '../../lib/services/gatewayService';
import { CustomerOrderDocumentActions } from './orders/CustomerOrderDocumentActions';
import { CustomerOrderTemplatePicker } from './orders/CustomerOrderTemplatePicker';
import { useCustomerOrderTemplates } from '../../lib/hooks/useCustomerOrderTemplates';
import React, { useEffect, useState } from 'react';
import {
  X,
  User,
  Package,
  Truck,
  FileText,
  Calendar,
  CheckCircle2,
  XCircle,
  Loader2,
  Phone,
  Mail,
  AlertCircle,
  ChevronDown,
  History,
  UserCheck,
  Pencil,
  Printer,
  RefreshCw,
  ExternalLink,
  Split,
  MoreHorizontal,
  Tag,
  Home
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../AppProvider';
import { supabase } from '../../lib/supabase';
import ManualOrderCreateModal from './ManualOrderCreateModal';
import { useManualOrderStore } from '../../lib/stores/manualOrderStore';
import { useUnifiedOrderStore } from '../../lib/stores/unifiedOrderStore';
import { useUserStore } from '../../lib/stores/userStore';
import { formatCurrency, formatDate } from '../../lib/utils';
import { MANUAL_ORDER_STATUS_CONFIG } from '../../lib/types/orders';
import type { ManualOrderStatus, OrderItemInput, OrderItem } from '../../lib/types/orders';
import { OrderStatusHistory } from './OrderStatusHistory';
import { OrderProductSelector } from './OrderProductSelector';
import { useProductCommercialNames, useCommercialNameVisible } from '../../lib/hooks/useProductCommercialNames';
import { useWarehouseSettings } from '../../lib/hooks/useWarehouseSettings';
import { useOrderProcessingNotes } from '../../lib/deals/lineProcessingNotes';
import { useDdtPrintTemplateStore, type DdtPrintTemplate } from '../../lib/stores/ddtPrintTemplateStore';
import { DdtPrintTemplateModal } from './ddts/DdtPrintTemplateModal';
import { loadManualOrderPrintData, printManualOrder } from './orders/ManualOrderPrint';
import { CommercialNameLine } from './ui/CommercialNameLine';
import DealOrderSplitModal from '../deals/DealOrderSplitModal';
import { OrderTrackingSection } from './orders/OrderTrackingSection';
import { storeOrderRef } from '../../lib/utils/unifiedOrderQuery';
import { OrderLabelsModal } from './orders/OrderLabelsModal';
import { StoreShipmentSection } from './orders/StoreShipmentSection';
import { StoreOrderNotes } from './orders/StoreOrderNotes';
import { StoreStatusBadges } from './orders/StoreStatusBadges';
import { storeStatusBadges } from '../../lib/commerce/storeOrderStatus';
import { clientCardPath, orderClientMatchText } from '../../lib/commerce/orderClientMatch';
import { getCachedClientStates } from '../../lib/stores/referenceDataCache';
import { DealWarehouseInfoCard } from './orders/DealWarehouseInfoCard';
import { loadOrdersDelivery, type OrderDelivery } from '../../lib/services/orderDeliveryService';
import { OUTBOUND_DDT_STATUS_LABELS } from '../../lib/types/manufacturing';
import { formatQuantity } from '../../lib/utils/quantityFormat';
import { resolveProductUnit } from '../../lib/constants/units';

/** Riga proprietà (etichetta a sinistra, valore a destra), stile Notion */
function PropertyRow({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      <div className="w-24 shrink-0 flex items-center gap-1.5 text-xs text-[#787774] dark:text-gray-400 pt-0.5">
        {icon}
        {label}
      </div>
      <div className="flex-1 min-w-0 text-sm text-[#37352F] dark:text-white">{children}</div>
    </div>
  );
}

interface ManualOrderDetailProps {
  orderId: string;
  organizationId: string;
  onClose: () => void;
  /** Ordine cambiato (conferma, stato, righe, assegnatario…): chi apre il dettaglio ricarica il suo elenco */
  onOrderUpdated?: () => void;
}

export default function ManualOrderDetail({
  orderId,
  organizationId,
  onClose,
  onOrderUpdated
}: ManualOrderDetailProps) {
  const {
    currentOrder,
    loading,
    error,
    fetchOrderById,
    confirmOrder,
    cancelOrder,
    updateStatus,
    updateOrderItems,
    refreshOrderFromDeal
  } = useManualOrderStore();
  const navigate = useNavigate();
  const orderDocumentConfig = customerOrderSettings(useSettingsStore(s=>s.settings));
  const { confirm, toast } = useApp();
  // Modifica completa dell'ordine in bozza (cliente, prodotti, spedizione, note)
  const [showEditOrderModal, setShowEditOrderModal] = useState(false);
  const [isRefreshingFromDeal, setIsRefreshingFromDeal] = useState(false);
  // Divisione in più ordini dell'offerta da cui è nato l'ordine
  const [showSplitModal, setShowSplitModal] = useState(false);

  const { updateOrderAssignee } = useUnifiedOrderStore();
  const { users, fetchUsers } = useUserStore();

  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showLabelsModal, setShowLabelsModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [userId, setUserId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'details' | 'history'>('details');

  // Assignee state
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [isUpdatingAssignee, setIsUpdatingAssignee] = useState(false);

  // Edit items state
  const [showEditItemsModal, setShowEditItemsModal] = useState(false);
  const [editItems, setEditItems] = useState<OrderItemInput[]>([]);
  const [isSavingItems, setIsSavingItems] = useState(false);

  // Nome commerciale accanto al nome magazzino (solo org con «Nome commerciale» attivo)
  const { commercialNameFor } = useProductCommercialNames((currentOrder?.items || []).map((i: OrderItem) => i.product_id));
  const showCommercialName = useCommercialNameVisible();
  // Nota «UM: …» sotto l'articolo nella stampa: si toglie per organizzazione (Impostazioni → Magazzino → Documenti e stampe)
  // product_label_operational: etichette QR dall'ordine (Say Farm)
  const { order_print_hide_unit_note: orderPrintHideUnitNote, line_processing_notes_enabled: lineNotesEnabled, line_packaging_split_enabled: packagingSplitEnabled, product_label_operational: operationalLabels } = useWarehouseSettings();
  // Note di lavorazione scritte sulle righe dell'offerta (lette dall'offerta, non copiate)
  const processingNotes = useOrderProcessingNotes(currentOrder?.deal_id ? orderId : null, lineNotesEnabled || packagingSplitEnabled);

  // Stampa / PDF conferma d'ordine: intestazione e lingua dal template di stampa
  // (gli stessi dei DDT). Con più template attivi si sceglie quale usare.
  const { templates: printTemplates, loaded: printTemplatesLoaded, fetchTemplates: fetchPrintTemplates } = useDdtPrintTemplateStore();
  useEffect(() => {
    if (!printTemplatesLoaded) fetchPrintTemplates();
  }, [printTemplatesLoaded, fetchPrintTemplates]);
  const activePrintTemplates = printTemplates.filter(t => t.is_active);
  const [showPrintTemplatePicker, setShowPrintTemplatePicker] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  // Template documento dell'org (Template documenti → Ordini cliente): il PDF
  // viene da flusia-core. Letti anche a flusso ordini spento: se l'org ne ha,
  // la stampante li propone (vedi handlePrintClick).
  const { templates: orderTemplates, loaded: orderTemplatesLoaded } = useCustomerOrderTemplates(organizationId, true);
  const [showOrderTemplatePicker, setShowOrderTemplatePicker] = useState(false);

  // Evasione con i DDT in uscita («Da ordine»): DDT collegati e quantità per riga.
  // Compare solo se l'ordine ha DDT collegati; se flusia-core non risponde non si mostra.
  const [delivery, setDelivery] = useState<OrderDelivery | null>(null);
  // importi nella valuta dell'ordine (gli ordini dei negozi possono essere in USD, GBP...): mai il simbolo dell'euro fisso
  const money = (v: number) => formatCurrency(v, { currency: currentOrder?.currency || 'EUR' });
  const refunded = Number((currentOrder as { refunded_amount?: number | string | null } | null)?.refunded_amount ?? 0);
  const deliveryOrderId = currentOrder?.id;
  const deliveryOrderStatus = currentOrder?.status;
  useEffect(() => {
    setDelivery(null);
    if (!deliveryOrderId) return;
    let cancelled = false;
    loadOrdersDelivery({ organizationId, orderIds: [deliveryOrderId] })
      .then(([o]) => { if (!cancelled) setDelivery(o && o.ddts.length > 0 ? o : null); })
      .catch(err => console.warn('Evasione ordine dai DDT non disponibile:', err));
    return () => { cancelled = true; };
  }, [deliveryOrderId, deliveryOrderStatus, organizationId]);
  const deliveryByItem = new Map((delivery?.items || []).map(i => [i.id, i]));
  // `unit` decide il punto delle migliaia (a pezzi niente punto); qui l'unità non si mostra
  const fmtQta = (q: number, unit?: string | null) => formatQuantity(q, { unit, unitDisplay: 'none' });
  // Unità della riga d'ordine; senza, la UDM 1 dell'articolo letta dall'evasione (se caricata)
  const unitOfItem = (item: OrderItem) => {
    if (item.sale_unit) return item.sale_unit;
    const d = item.id ? deliveryByItem.get(item.id) : undefined;
    if (d) return resolveProductUnit({ unit_of_measure_1: d.unit_of_measure });
    return item.product ? resolveProductUnit(item.product) : undefined;
  };

  // Indirizzo di spedizione dell'offerta diverso da quello dell'ordine: solo avviso,
  // «Aggiorna dall'offerta» non lo riallinea (si cambia a mano con Modifica spedizione)
  const [dealShippingMismatch, setDealShippingMismatch] = useState<string | null>(null);
  const orderDealId = currentOrder?.deal_id;
  const orderStatus = currentOrder?.status;
  const orderLocationId = currentOrder?.shipping_location_id ?? null;
  useEffect(() => {
    setDealShippingMismatch(null);
    if (!orderDealId || orderStatus !== 'draft') return;
    let cancelled = false;
    (async () => {
      const { data: deal, error: dealErr } = await supabase
        .from('deals')
        .select('shipping_location_id')
        .eq('id', orderDealId)
        .maybeSingle();
      // Colonna assente (migration non applicata) o offerta non leggibile: nessun avviso
      if (dealErr || !deal) return;
      const dealLocationId = (deal as { shipping_location_id: string | null }).shipping_location_id ?? null;
      if (dealLocationId === orderLocationId) return;
      let label = 'Sede del cliente';
      if (dealLocationId) {
        const { data: loc } = await supabase
          .from('client_operational_locations')
          .select('name, address, city')
          .eq('id', dealLocationId)
          .maybeSingle();
        const l = loc as { name: string | null; address: string | null; city: string | null } | null;
        label = [l?.name || l?.address, l?.city].filter(Boolean).join(' · ') || 'un altro indirizzo';
      }
      if (!cancelled) setDealShippingMismatch(label);
    })();
    return () => { cancelled = true; };
  }, [orderDealId, orderStatus, orderLocationId]);

  // Ordine nato da un'offerta: righe e cliente si riallineano all'offerta (non si modificano qui)
  const handleRefreshFromDeal = async () => {
    if (!currentOrder || !userId) return;
    const ok = await confirm({
      type: 'warning',
      title: "Aggiorna dall'offerta",
      message: "Le righe dell'offerta che appartengono a questo ordine riprendono prezzi, nomi e quantità attuali dell'offerta (se una riga è divisa con altri ordini la quantità non aumenta); il cliente si allinea all'offerta e i totali vengono ricalcolati. Le righe nuove dell'offerta non entrano: si assegnano con «Dividi in ordini». Spedizione, indirizzi e note restano invariati."
        + (dealShippingMismatch ? ` Attenzione: nell'offerta l'indirizzo di spedizione è «${dealShippingMismatch}», diverso da quello dell'ordine; se serve, cambialo con Modifica spedizione.` : ''),
      confirmText: 'Aggiorna',
      cancelText: 'Annulla',
    });
    if (!ok) return;
    setIsRefreshingFromDeal(true);
    const success = await refreshOrderFromDeal(currentOrder.id, organizationId, userId);
    setIsRefreshingFromDeal(false);
    if (success) { toast.success("Ordine aggiornato dall'offerta"); onOrderUpdated?.(); }
    else toast.error(useManualOrderStore.getState().error || "Aggiornamento dall'offerta non riuscito");
  };

  const printOrder = async (template: DdtPrintTemplate | null) => {
    setShowPrintTemplatePicker(false);
    if (!currentOrder) return;
    // Finestra aperta subito, dentro il clic: dopo gli await il browser la bloccherebbe
    const win = window.open('', '_blank', 'width=900,height=700');
    if (win) win.document.write('<p style="font-family:Arial;padding:16px">Preparazione conferma d\'ordine…</p>');
    setIsPrinting(true);
    try {
      const data = {
        ...(await loadManualOrderPrintData(currentOrder, showCommercialName)),
        hideUnitNote: orderPrintHideUnitNote,
        // Confezionamento per riga (Say Farm) sotto l'articolo nella stampa
        packagingByOrderItem: packagingSplitEnabled ? processingNotes.packagingShortByOrderItem : undefined,
      };
      if (win) win.document.open();
      printManualOrder(template, data, win);
    } catch (err) {
      console.error('Stampa ordine non riuscita', err);
      win?.close();
    } finally {
      setIsPrinting(false);
    }
  };

  // templateId assente = template predefinito delle impostazioni ordini
  const openCustomerOrderPdf = async (templateId?: string) => {
      if (isPrinting) return;
      // Finestra aperta subito, dentro il clic: dopo gli await il browser la bloccherebbe
      const win=window.open('', '_blank', 'width=900,height=700');
      if (!win) { toast.error('Consenti l’apertura della finestra per visualizzare il PDF'); return; }
      win.document.body.textContent='Preparazione documento…';
      setIsPrinting(true);
      try {
        const {data,error}=await coreCall<{pdf_base64:string}>('orders/warehouse-document',{body:{order_id:orderId,action:'pdf',...(templateId?{template_id:templateId}:{})}});
        if(error || !data) throw new Error(error?.message || 'Documento non disponibile');
        const bytes=Uint8Array.from(atob(data.pdf_base64),c=>c.charCodeAt(0));
        const url=URL.createObjectURL(new Blob([bytes],{type:'application/pdf'}));
        win.location.href=url;
        setTimeout(()=>URL.revokeObjectURL(url),60000);
      } catch(error) { win.close(); toast.error(error instanceof Error ? error.message : 'PDF non disponibile'); }
      finally {setIsPrinting(false);}
  };

  const handlePrintClick = async () => {
    if (orderDocumentConfig.enabled) {
      if (isPrinting) return;
      if (orderTemplates.length > 1) { setShowOrderTemplatePicker(true); return; }
      await openCustomerOrderPdf();
      return;
    }
    // Flusso spento ma l'org ha template ordine (es. Ideariso): si sceglie tra
    // quelli e la stampa standard di sempre, che resta disponibile
    if (orderTemplates.length > 0) { setShowOrderTemplatePicker(true); return; }
    printLegacyConfirmation();
  };

  // Stampa conferma d'ordine generata nel browser, con intestazione dai template DDT
  const printLegacyConfirmation = () => {
    if (activePrintTemplates.length > 1) setShowPrintTemplatePicker(true);
    else printOrder(activePrintTemplates[0] || null);
  };

  // Stati Lead/Cliente: il nome del cliente apre la scheda nella pagina giusta (Lead o Clienti)
  const [clientStates, setClientStates] = useState<Array<{ id: string; name: string; is_lead: boolean | null }>>([]);
  useEffect(() => {
    getCachedClientStates(organizationId)
      .then((s) => setClientStates((s || []) as Array<{ id: string; name: string; is_lead: boolean | null }>))
      .catch(() => setClientStates([]));
  }, [organizationId]);

  useEffect(() => {
    fetchOrderById(orderId, organizationId);
    loadUserId();
    fetchUsers();
  }, [orderId, organizationId]);

  const loadUserId = async () => {
    const { data } = await supabase.auth.getUser();
    if (data.user) {
      setUserId(data.user.id);
    }
  };

  const handleConfirmOrder = async () => {
    if (!userId) return;
    await confirmOrder(orderId, organizationId, userId);
    onOrderUpdated?.();
  };

  const handleCancelOrder = async () => {
    if (!userId || !cancelReason.trim()) return;
    await cancelOrder(orderId, cancelReason, organizationId, userId);
    setShowCancelModal(false);
    setCancelReason('');
    onOrderUpdated?.();
  };

  const handleStatusChange = async (newStatus: ManualOrderStatus) => {
    if (!userId) return;
    await updateStatus(orderId, newStatus, organizationId, userId);
    setShowStatusDropdown(false);
    onOrderUpdated?.();
  };

  const openEditItems = () => {
    if (!currentOrder?.items) return;
    setEditItems(currentOrder.items.map((item: OrderItem) => ({
      product_id: item.product_id,
      variation_id: item.variation_id,
      product_name: item.product_name,
      product_sku: item.product_sku,
      quantity: item.quantity,
      unit_price: item.unit_price,
      discount_amount: item.discount_amount,
      tax_rate: item.tax_rate,
      notes: item.notes,
      line_type: item.line_type === 'custom' ? 'custom' : undefined,
    })));
    setShowEditItemsModal(true);
  };

  const handleSaveItems = async () => {
    if (!userId || editItems.length === 0) return;
    setIsSavingItems(true);
    const success = await updateOrderItems(orderId, editItems, organizationId, userId);
    setIsSavingItems(false);
    if (success) {
      setShowEditItemsModal(false);
      onOrderUpdated?.();
    }
  };

  const handleAssigneeChange = async (assigneeId: string | null) => {
    setIsUpdatingAssignee(true);
    setShowAssigneeDropdown(false);
    await updateOrderAssignee(orderId, 'manual', assigneeId, organizationId);
    // Refresh current order to get updated data
    await fetchOrderById(orderId, organizationId);
    setIsUpdatingAssignee(false);
    onOrderUpdated?.();
  };

  // Get current assignee from order data
  const currentAssignee = users.find(u => u.id === (currentOrder as any)?.assigned_to);

  const getStatusConfig = (status: string) => {
    return MANUAL_ORDER_STATUS_CONFIG[status as ManualOrderStatus] || {
      label: status,
      color: 'text-gray-700',
      bgColor: 'bg-gray-100'
    };
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !currentOrder) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-6">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-[#787774] dark:text-gray-400 text-center">
          {error || 'Ordine non trovato'}
        </p>
        <button
          onClick={onClose}
          className="mt-4 px-4 py-2 bg-[#37352F] text-white rounded hover:bg-[#2f2d27] transition-colors"
        >
          Chiudi
        </button>
      </div>
    );
  }

  const statusConfig = getStatusConfig(currentOrder.status);

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 p-4 border-b border-[rgba(55,53,47,0.09)] dark:border-gray-800">
        <div className="min-w-0">
          <p className="text-xs text-[#787774] dark:text-gray-400">Ordine</p>
          {(() => {
            // Ordini dei negozi: in evidenza il numero del negozio (es. WooCommerce #113520),
            // ORD-... sotto come numero interno Flusia
            const o = currentOrder as { channel?: string | null; external_number?: string | null };
            const ref = storeOrderRef(o.channel, o.external_number);
            return (
              <>
                <h2 className="text-lg font-bold text-[#37352F] dark:text-white whitespace-nowrap truncate" title={ref ? "Numero dell'ordine nel negozio online" : currentOrder.order_number}>
                  {ref ?? currentOrder.order_number}
                </h2>
                {ref && <p className="text-xs text-[#787774] dark:text-gray-400" title="Numero interno Flusia">N. interno {currentOrder.order_number}</p>}
              </>
            );
          })()}
          <div className="flex flex-wrap items-center gap-2 mt-1">
            {/* Ordini dei negozi: prima lo stato come nel negozio, poi quello del magazzino Flusia */}
            {(() => {
              const o = currentOrder as { channel?: string | null; external_financial_status?: string | null; external_fulfillment_status?: string | null };
              const badges = storeStatusBadges({ channel: o.channel, status: currentOrder.status, rawFinancial: o.external_financial_status, rawFulfillment: o.external_fulfillment_status });
              return badges ? (
                <>
                  <StoreStatusBadges badges={badges} title="Stato nel negozio" />
                  <span className="text-xs text-[#787774] dark:text-gray-400">Magazzino:</span>
                </>
              ) : null;
            })()}
            {/* Status dropdown - compact like WooCommerce */}
            <div className="relative">
              <button
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.bgColor} ${statusConfig.color} hover:opacity-80 transition-opacity`}
              >
                {statusConfig.label}
                <ChevronDown className="w-3 h-3" />
              </button>

              {showStatusDropdown && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-[rgba(55,53,47,0.09)] dark:border-gray-700 z-50">
                  <div className="py-1">
                    {(['draft', 'confirmed', 'processing', 'prepared', 'shipped', 'invoiced', 'completed'] as ManualOrderStatus[]).map(status => {
                      const config = getStatusConfig(status);
                      return (
                        <button
                          key={status}
                          onClick={() => handleStatusChange(status)}
                          disabled={status === currentOrder.status}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          <span className={`w-2 h-2 rounded-full ${config.bgColor}`} />
                          {config.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            {currentOrder.deal_id && (
              <span className="text-xs text-[#787774] dark:text-gray-400">da offerta</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {/* Azione principale in evidenza */}
          {currentOrder.status === 'draft' && (
            <button
              onClick={handleConfirmOrder}
              className="flex items-center gap-1.5 px-3 py-1.5 mr-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
              title="Conferma ordine"
            >
              <CheckCircle2 className="w-4 h-4" />
              Conferma
            </button>
          )}
          {/* Stampa / PDF: dal dialogo di stampa del browser si salva anche in PDF */}
          <button
            onClick={handlePrintClick}
            // Finché l'elenco template non è letto non si sa quale stampa proporre
            disabled={isPrinting || !orderTemplatesLoaded}
            className="p-2 text-[#787774] hover:text-[#37352F] dark:hover:text-white hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-800 rounded-lg disabled:opacity-50"
            title="Stampa / PDF conferma d'ordine"
            aria-label="Stampa / PDF conferma d'ordine"
          >
            {isPrinting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Printer className="w-5 h-5" />}
          </button>
          {/* Modifica ordine in bozza: form completo (da offerta: solo spedizione e note) */}
          {currentOrder.status === 'draft' && (
            <button
              onClick={() => setShowEditOrderModal(true)}
              className="p-2 text-[#787774] hover:text-[#37352F] dark:hover:text-white hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-800 rounded-lg"
              title={currentOrder.deal_id ? 'Modifica spedizione e note' : 'Modifica ordine'}
              aria-label={currentOrder.deal_id ? 'Modifica spedizione e note' : 'Modifica ordine'}
            >
              <Pencil className="w-5 h-5" />
            </button>
          )}
          {/* Azioni secondarie: l'annullamento non sta più accanto alla chiusura */}
          {!['completed', 'cancelled'].includes(currentOrder.status) && (
            <div className="relative">
              <button
                onClick={() => setShowActionsMenu(v => !v)}
                className="p-2 text-[#787774] hover:text-[#37352F] dark:hover:text-white hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-800 rounded-lg"
                title="Altre azioni"
                aria-label="Altre azioni"
              >
                <MoreHorizontal className="w-5 h-5" />
              </button>
              {showActionsMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowActionsMenu(false)} />
                  <div className="absolute top-full right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-[rgba(55,53,47,0.09)] dark:border-gray-700 z-50 py-1">
                    <button
                      onClick={() => { setShowActionsMenu(false); setShowCancelModal(true); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 text-left"
                    >
                      <XCircle className="w-4 h-4" />
                      Annulla ordine
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          <div className="w-px h-5 bg-[rgba(55,53,47,0.12)] dark:bg-gray-700 mx-1" />
          <button
            onClick={onClose}
            className="p-2 text-[#787774] hover:text-[#37352F] dark:hover:text-white hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-800 rounded-lg"
            title="Chiudi"
            aria-label="Chiudi"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700 px-4">
        <button
          onClick={() => setActiveTab('details')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'details'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-[#787774] hover:text-[#37352F] dark:hover:text-white'
          }`}
        >
          <FileText className="w-4 h-4" />
          Dettagli
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'history'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-[#787774] hover:text-[#37352F] dark:hover:text-white'
          }`}
        >
          <History className="w-4 h-4" />
          Cronologia Stati
        </button>
      </div>

      {/* Content */}
      {activeTab === 'details' ? (
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Ordine annullato: motivo in evidenza */}
        {currentOrder.status === 'cancelled' && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2.5 text-sm text-red-800 dark:text-red-300">
            <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              Ordine annullato{currentOrder.cancelled_at ? ` il ${formatDate(currentOrder.cancelled_at)}` : ''}
              {currentOrder.cancel_reason ? `: ${currentOrder.cancel_reason}` : ''}
            </span>
          </div>
        )}

        {/* Proprietà: chi, per chi, da dove, quando */}
        <div className="divide-y divide-[rgba(55,53,47,0.06)] dark:divide-gray-800">
          <PropertyRow icon={<User className="w-3.5 h-3.5" />} label="Cliente">
            {currentOrder.client ? (
              <>
                {(() => {
                  // Dove sta la scheda: i Lead (es. ordine del negozio non pagato) non sono nella pagina Clienti
                  const stato = clientStates.find(s => s.id === currentOrder.client!.stato_cliente);
                  const isLead = stato?.is_lead === true;
                  const motivo = orderClientMatchText((currentOrder as { client_match_method?: string | null }).client_match_method);
                  return (
                    <>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <button
                          onClick={() => navigate(clientCardPath(currentOrder.client!.id, isLead))}
                          className="inline-flex items-center gap-1 text-left font-medium text-[#37352F] dark:text-white hover:underline"
                          title={isLead ? 'Apri la scheda in Lead' : 'Apri la scheda in Clienti'}
                        >
                          <span className="truncate">{currentOrder.client!.business_name}</span>
                          <ExternalLink className="w-3 h-3 shrink-0 text-[#787774]" />
                        </button>
                        {stato && (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${isLead ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300' : 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300'}`}>
                            {stato.name}
                          </span>
                        )}
                      </div>
                      {motivo && <p className="text-xs text-[#9B9A97] dark:text-gray-500">{motivo}</p>}
                    </>
                  );
                })()}
                {(currentOrder.client.email || currentOrder.client.phone || currentOrder.client.vat_number) && (
                  <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-[#787774] dark:text-gray-400">
                    {currentOrder.client.email && (
                      <span className="flex items-center gap-1 min-w-0"><Mail className="w-3 h-3 shrink-0" /><span className="truncate">{currentOrder.client.email}</span></span>
                    )}
                    {currentOrder.client.phone && (
                      <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{currentOrder.client.phone}</span>
                    )}
                    {currentOrder.client.vat_number && <span>P.IVA {currentOrder.client.vat_number}</span>}
                  </div>
                )}
              </>
            ) : (
              <span className="text-[#9B9A97] dark:text-gray-500">Non specificato</span>
            )}
          </PropertyRow>

          <PropertyRow icon={<UserCheck className="w-3.5 h-3.5" />} label="Operatore">
            <div className="relative -my-1 -ml-1.5">
              <button
                onClick={() => setShowAssigneeDropdown(!showAssigneeDropdown)}
                disabled={isUpdatingAssignee}
                className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-gray-800 transition-colors"
              >
                {isUpdatingAssignee ? (
                  <Loader2 className="w-4 h-4 animate-spin text-[#787774]" />
                ) : currentAssignee ? (
                  <>
                    {currentAssignee.avatar_url ? (
                      <img src={currentAssignee.avatar_url} alt={currentAssignee.full_name} className="w-5 h-5 rounded-full object-cover" />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                        <User className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                      </div>
                    )}
                    <span className="text-sm text-[#37352F] dark:text-white">{currentAssignee.full_name}</span>
                  </>
                ) : (
                  <span className="text-sm text-[#9B9A97] dark:text-gray-500">Non assegnato</span>
                )}
                <ChevronDown className="w-3 h-3 text-[#787774]" />
              </button>

              {showAssigneeDropdown && (
                <div className="absolute top-full left-0 mt-1 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-[rgba(55,53,47,0.09)] dark:border-gray-700 z-50 max-h-60 overflow-y-auto">
                  <button
                    onClick={() => handleAssigneeChange(null)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-700 transition-colors border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700"
                  >
                    <div className="w-5 h-5 rounded-full bg-[rgba(55,53,47,0.08)] dark:bg-gray-700 flex items-center justify-center">
                      <X className="w-3 h-3 text-[#787774]" />
                    </div>
                    <span className="text-sm text-[#787774] dark:text-gray-400">Nessuno</span>
                  </button>
                  {users.map(user => (
                    <button
                      key={user.id}
                      onClick={() => handleAssigneeChange(user.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-700 transition-colors ${
                        user.id === (currentOrder as any)?.assigned_to ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                      }`}
                    >
                      {user.avatar_url ? (
                        <img src={user.avatar_url} alt={user.full_name} className="w-5 h-5 rounded-full object-cover" />
                      ) : (
                        <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                          <User className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                        </div>
                      )}
                      <span className="text-sm text-[#37352F] dark:text-white truncate">{user.full_name}</span>
                      {user.id === (currentOrder as any)?.assigned_to && (
                        <span className="ml-auto text-xs text-blue-600 dark:text-blue-400">✓</span>
                      )}
                    </button>
                  ))}
                  {users.length === 0 && (
                    <div className="px-3 py-3 text-center text-sm text-[#787774] dark:text-gray-400">Nessun operatore</div>
                  )}
                </div>
              )}
            </div>
          </PropertyRow>

          {currentOrder.deal && (
            <PropertyRow icon={<FileText className="w-3.5 h-3.5" />} label="Offerta">
              <button
                onClick={() => navigate(`/deals/${currentOrder.deal!.id}`)}
                className="inline-flex items-center gap-1 text-left font-medium text-blue-700 dark:text-blue-400 hover:underline"
              >
                <span className="truncate">{currentOrder.deal.title}</span>
                <ExternalLink className="w-3 h-3 shrink-0" />
              </button>
            </PropertyRow>
          )}

          <PropertyRow icon={<Calendar className="w-3.5 h-3.5" />} label="Date">
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[#37352F] dark:text-white">
              {/* data dell'ordine (per i negozi quella del negozio, anche se cambiata dopo), non quella della riga in Flusia */}
              <span><span className="text-[#787774] dark:text-gray-400">Data ordine</span> {formatDate(currentOrder.order_date || currentOrder.created_at)}</span>
              {currentOrder.confirmed_at && (
                <span><span className="text-[#787774] dark:text-gray-400">Confermato</span> {formatDate(currentOrder.confirmed_at)}</span>
              )}
              {currentOrder.shipped_at && (
                <span><span className="text-[#787774] dark:text-gray-400">Spedito</span> {formatDate(currentOrder.shipped_at)}</span>
              )}
              {currentOrder.completed_at && (
                <span><span className="text-[#787774] dark:text-gray-400">Completato</span> {formatDate(currentOrder.completed_at)}</span>
              )}
            </div>
          </PropertyRow>
        </div>

        {/* Ordine in bozza nato da offerta: come si aggiorna, in un solo riquadro */}
        {currentOrder.deal && currentOrder.status === 'draft' && (
          <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 px-3 py-2.5 space-y-2">
            <p className="text-xs text-blue-900 dark:text-blue-200">
              Prodotti e cliente arrivano dall'offerta: modificali lì, poi usa «Aggiorna dall'offerta».
            </p>
            {dealShippingMismatch && (
              <div className="flex items-start gap-1.5 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-2.5 py-2 text-xs text-amber-800 dark:text-amber-300">
                <AlertCircle className="w-3.5 h-3.5 mt-px flex-shrink-0" />
                <span>
                  Nell'offerta l'indirizzo di spedizione è «{dealShippingMismatch}», diverso da quello dell'ordine.
                  «Aggiorna dall'offerta» non lo cambia: se serve, modificalo dalla spedizione dell'ordine.
                </span>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleRefreshFromDeal}
                disabled={isRefreshingFromDeal}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isRefreshingFromDeal ? 'animate-spin' : ''}`} />
                Aggiorna dall'offerta
              </button>
              {/* Solo se l'ordine ha almeno 2 unità da dividere: niente divisioni senza fine */}
              {(currentOrder.items || []).reduce((sum: number, i: OrderItem) => sum + Math.max(1, Math.floor(Number(i.quantity) + 0.0005)), 0) >= 2 && (
                <button
                  onClick={() => setShowSplitModal(true)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-blue-700 dark:text-blue-300 bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-800 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                >
                  <Split className="w-3.5 h-3.5" />
                  Dividi in ordini
                </button>
              )}
            </div>
          </div>
        )}

        {/* Spedizione: dove va e con quale tracking */}
        <section className="space-y-1">
          <h3 className="text-sm font-medium text-[#37352F] dark:text-white flex items-center gap-2">
            <Truck className="w-4 h-4" />
            Spedizione
          </h3>
          <div className="divide-y divide-[rgba(55,53,47,0.06)] dark:divide-gray-800">
            {/* Fatturazione dell'ordine (dal negozio: com'e' ora sull'ordine, anche se diversa dalla scheda cliente) */}
            {(() => {
              const b = currentOrder.billing_address;
              const nome = b ? [b.first_name, b.last_name].filter(Boolean).join(' ') : '';
              const citta = b ? [[b.postcode, b.city].filter(Boolean).join(' '), b.state ? `(${b.state})` : ''].filter(Boolean).join(' ') : '';
              if (!b || !(b.company || nome || b.address_1 || citta || b.email || b.phone)) return null;
              return (
                <PropertyRow icon={<FileText className="w-3.5 h-3.5" />} label="Fatturazione">
                  {(b.company || nome) && <p className="font-medium text-[#37352F] dark:text-white">{b.company || nome}</p>}
                  {b.company && nome && <p className="text-[#37352F] dark:text-gray-200">{nome}</p>}
                  {b.address_1 && <p className="text-[#37352F] dark:text-gray-200">{b.address_1}{b.address_2 ? `, ${b.address_2}` : ''}</p>}
                  {citta && <p className="text-[#787774] dark:text-gray-400">{citta}</p>}
                  {(b.email || b.phone) && (
                    <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-[#787774] dark:text-gray-400">
                      {b.email && <span className="flex items-center gap-1 min-w-0"><Mail className="w-3 h-3 shrink-0" /><span className="truncate">{b.email}</span></span>}
                      {b.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{b.phone}</span>}
                    </div>
                  )}
                </PropertyRow>
              );
            })()}
            <PropertyRow icon={<Home className="w-3.5 h-3.5" />} label="Consegna">
              {(() => {
                const a = currentOrder.shipping_address;
                const nome = a ? [a.first_name, a.last_name].filter(Boolean).join(' ') : '';
                const citta = a ? [[a.postcode, a.city].filter(Boolean).join(' '), a.state ? `(${a.state})` : ''].filter(Boolean).join(' ') : '';
                if (!a || !(a.company || nome || a.address_1 || citta)) {
                  return <span className="text-[#9B9A97] dark:text-gray-500">Sede del cliente</span>;
                }
                return (
                  <>
                    {(a.company || nome) && <p className="font-medium text-[#37352F] dark:text-white">{a.company || nome}</p>}
                    {a.address_1 && <p className="text-[#37352F] dark:text-gray-200">{a.address_1}{a.address_2 ? `, ${a.address_2}` : ''}</p>}
                    {citta && <p className="text-[#787774] dark:text-gray-400">{citta}</p>}
                  </>
                );
              })()}
            </PropertyRow>
            {currentOrder.shipping_method && (
              <PropertyRow icon={<Package className="w-3.5 h-3.5" />} label="Metodo">
                {currentOrder.shipping_method}
              </PropertyRow>
            )}
            {/* Tracking: inseribile e modificabile in qualsiasi momento, anche prima della preparazione.
                Ordini arrivati da un negozio: la spedizione passa dal negozio (StoreShipmentSection), non a mano */}
            {(currentOrder as { commerce_store_id?: string | null }).commerce_store_id
              ? <StoreShipmentSection orderId={currentOrder.id} />
              : <OrderTrackingSection order={currentOrder} organizationId={organizationId} userId={userId} />}
            {delivery && (() => {
              const items = delivery.items;
              const EPS = 0.0005;
              const tuttoConsegnato = items.every(i => i.delivered >= i.quantity - EPS);
              const tuttoSpedito = items.every(i => i.shipped >= i.quantity - EPS);
              const qualcosaSpedito = items.some(i => i.shipped > EPS);
              const daEvadere = items.filter(i => i.remaining > EPS).length;
              const evasione = tuttoConsegnato
                ? { label: 'Consegnato', cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300' }
                : tuttoSpedito
                  ? { label: 'Spedito', cls: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/25 dark:text-indigo-300' }
                  : qualcosaSpedito
                    ? { label: 'Spedito in parte', cls: 'bg-blue-50 text-blue-700 dark:bg-blue-900/25 dark:text-blue-300' }
                    : { label: 'In DDT, da spedire', cls: 'bg-amber-50 text-amber-700 dark:bg-amber-900/25 dark:text-amber-300' };
              return (
                <PropertyRow icon={<FileText className="w-3.5 h-3.5" />} label="DDT">
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${evasione.cls}`}>{evasione.label}</span>
                      <span className="text-xs text-[#787774] dark:text-gray-400">
                        {daEvadere === 0 ? 'Tutte le righe sono in DDT' : `${daEvadere} ${daEvadere === 1 ? 'riga' : 'righe'} ancora da mettere in DDT`}
                      </span>
                    </div>
                    {delivery.ddts.map(d => (
                      <p key={d.id} className="text-sm text-[#37352F] dark:text-gray-200">
                        DDT {d.ddt_number ? `n. ${d.ddt_number}` : '(senza numero)'}
                        {d.ddt_date && <span className="text-[#787774] dark:text-gray-400"> · {formatDate(d.ddt_date)}</span>}
                        <span className="text-xs text-[#787774] dark:text-gray-400"> · {OUTBOUND_DDT_STATUS_LABELS[d.status as keyof typeof OUTBOUND_DDT_STATUS_LABELS] || d.status}</span>
                      </p>
                    ))}
                  </div>
                </PropertyRow>
              );
            })()}
          </div>
        </section>

        {/* Prodotti e totali in un unico blocco */}
        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-[#37352F] dark:text-white flex items-center gap-2">
              <Package className="w-4 h-4" />
              Prodotti
              <span className="px-1.5 py-0.5 text-[10px] font-medium bg-[rgba(55,53,47,0.08)] dark:bg-gray-700 text-[#787774] dark:text-gray-300 rounded-full">
                {currentOrder.items?.length || 0}
              </span>
            </h3>
            <div className="flex items-center gap-1">
              {/* Etichette QR degli articoli, senza aprire le schede prodotto */}
              {operationalLabels && (currentOrder.items?.length || 0) > 0 && (
                <button
                  onClick={() => setShowLabelsModal(true)}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-[#2383E2] hover:bg-[rgba(35,131,226,0.08)] rounded transition-colors"
                >
                  <Tag className="w-3 h-3" />
                  Stampa etichette
                </button>
              )}
              {/* Da offerta i prodotti si aggiornano dall'offerta, non da qui */}
              {currentOrder.status === 'draft' && !currentOrder.deal_id && (
                <button
                  onClick={openEditItems}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-[#787774] hover:text-[#37352F] dark:text-gray-400 dark:hover:text-white hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-800 rounded transition-colors"
                >
                  <Pencil className="w-3 h-3" />
                  Modifica
                </button>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-[rgba(55,53,47,0.09)] dark:border-gray-800 overflow-hidden">
            <div className="divide-y divide-[rgba(55,53,47,0.06)] dark:divide-gray-800">
              {currentOrder.items?.map((item, index) => (
                <div key={item.id || index} className="flex justify-between items-start gap-3 px-3 py-2.5">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#37352F] dark:text-white text-sm">{item.product_name}</p>
                    <CommercialNameLine commercialName={commercialNameFor(item.product_id)} productName={item.product_name} />
                    <p className="text-xs text-[#787774] dark:text-gray-400 mt-0.5">
                      {item.product_sku && <span>{item.product_sku} · </span>}
                      {fmtQta(item.quantity, unitOfItem(item))} × {money(item.unit_price)}
                    </p>
                    {item.id && processingNotes.byOrderItem[item.id] && (
                      <p className="mt-1 rounded bg-[rgba(55,53,47,0.04)] dark:bg-white/[0.05] px-2 py-1 text-xs text-[#37352F] dark:text-gray-200 whitespace-pre-line">
                        <span className="font-medium">Lavorazione: </span>{processingNotes.byOrderItem[item.id]}
                      </p>
                    )}
                    {packagingSplitEnabled && item.id && processingNotes.packagingByOrderItem[item.id] && (
                      <p className="mt-1 rounded bg-[rgba(55,53,47,0.04)] dark:bg-white/[0.05] px-2 py-1 text-xs text-[#37352F] dark:text-gray-200">
                        <span className="font-medium">Confezionamento: </span>{processingNotes.packagingByOrderItem[item.id]}
                      </p>
                    )}
                    {(() => {
                      const d = item.id ? deliveryByItem.get(item.id) : undefined;
                      if (!d) return null;
                      const um = d.unit_of_measure ? ` ${d.unit_of_measure}` : '';
                      const udm = resolveProductUnit({ unit_of_measure_1: d.unit_of_measure });
                      return (
                        <p className="mt-1 flex flex-wrap gap-x-2.5 gap-y-0.5 text-[11px] text-[#787774] dark:text-gray-400 tabular-nums">
                          <span>In DDT {fmtQta(d.in_ddt, udm)}{um}</span>
                          <span>Spediti {fmtQta(d.shipped, udm)}{um}</span>
                          <span>Consegnati {fmtQta(d.delivered, udm)}{um}</span>
                          <span className={d.remaining > 0.0005 ? 'font-medium text-amber-700 dark:text-amber-300' : ''}>
                            Da evadere {fmtQta(d.remaining, udm)}{um}
                          </span>
                        </p>
                      );
                    })()}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-medium text-[#37352F] dark:text-white text-sm tabular-nums">{money(item.line_total)}</p>
                    {item.discount_amount > 0 && (
                      <p className="text-xs text-green-600 tabular-nums">-{money(item.discount_amount)}</p>
                    )}
                  </div>
                </div>
              ))}
              {(!currentOrder.items || currentOrder.items.length === 0) && (
                <p className="px-3 py-4 text-center text-sm text-[#787774] dark:text-gray-400">Nessun prodotto</p>
              )}
            </div>

            <div className="bg-[rgba(55,53,47,0.03)] dark:bg-gray-800/60 border-t border-[rgba(55,53,47,0.09)] dark:border-gray-800 px-3 py-2.5 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-[#787774] dark:text-gray-400">Subtotale</span>
                <span className="text-[#37352F] dark:text-white tabular-nums">{money(currentOrder.subtotal)}</span>
              </div>
              {currentOrder.discount_amount > 0 && (
                <div className="flex justify-between">
                  <span className="text-[#787774] dark:text-gray-400">Sconto</span>
                  <span className="text-green-600 tabular-nums">-{money(currentOrder.discount_amount)}</span>
                </div>
              )}
              {currentOrder.shipping_cost > 0 && (
                <div className="flex justify-between">
                  <span className="text-[#787774] dark:text-gray-400">Spedizione</span>
                  <span className="text-[#37352F] dark:text-white tabular-nums">{money(currentOrder.shipping_cost)}</span>
                </div>
              )}
              {currentOrder.tax_amount > 0 && (
                <div className="flex justify-between">
                  <span className="text-[#787774] dark:text-gray-400">IVA</span>
                  <span className="text-[#37352F] dark:text-white tabular-nums">{money(currentOrder.tax_amount)}</span>
                </div>
              )}
              <div className="flex justify-between pt-1.5 mt-1 border-t border-[rgba(55,53,47,0.09)] dark:border-gray-700 text-base font-semibold">
                <span className="text-[#37352F] dark:text-white">Totale</span>
                <span className="text-[#37352F] dark:text-white tabular-nums">{money(currentOrder.total)}</span>
              </div>
              {refunded > 0 && (
                <>
                  <div className="flex justify-between">
                    <span className="text-[#787774] dark:text-gray-400">Rimborsato</span>
                    <span className="text-red-600 tabular-nums">-{money(refunded)}</span>
                  </div>
                  <div className="flex justify-between font-semibold">
                    <span className="text-[#37352F] dark:text-white">Netto</span>
                    <span className="text-[#37352F] dark:text-white tabular-nums">{money(Math.max(0, Number(currentOrder.total) - refunded))}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Indicazioni per il magazzino scritte sull'offerta */}
        {currentOrder.deal_id && <DealWarehouseInfoCard dealId={currentOrder.deal_id} />}

        {/* Note scritte nel negozio (privata / inviata al cliente), sola lettura */}
        {(currentOrder as { commerce_store_id?: string | null }).commerce_store_id && (
          <StoreOrderNotes orderId={currentOrder.id} refreshKey={currentOrder.updated_at} />
        )}

        {/* Note: la nota automatica «Ordine generato da offerta <id>» è già rappresentata dalla riga Offerta */}
        {(() => {
          const notaAutomatica = currentOrder.deal_id ? `Ordine generato da offerta ${currentOrder.deal_id}` : null;
          const noteInterne = currentOrder.internal_notes && currentOrder.internal_notes.trim() !== notaAutomatica
            ? currentOrder.internal_notes
            : null;
          if (!currentOrder.customer_notes && !noteInterne) return null;
          return (
            <section className="space-y-2">
              <h3 className="text-sm font-medium text-[#37352F] dark:text-white">Note</h3>
              {currentOrder.customer_notes && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg px-3 py-2.5">
                  <p className="text-xs text-yellow-700 dark:text-yellow-400 font-medium mb-0.5">Note cliente</p>
                  <p className="text-sm text-[#37352F] dark:text-white whitespace-pre-line">{currentOrder.customer_notes}</p>
                </div>
              )}
              {noteInterne && (
                <div className="bg-[rgba(55,53,47,0.04)] dark:bg-gray-800 rounded-lg px-3 py-2.5">
                  <p className="text-xs text-[#787774] dark:text-gray-400 font-medium mb-0.5">Note interne</p>
                  <p className="text-sm text-[#37352F] dark:text-white whitespace-pre-line">{noteInterne}</p>
                </div>
              )}
            </section>
          );
        })()}
      </div>
      ) : (
      <div className="flex-1 overflow-y-auto p-4">
        <CustomerOrderDocumentActions orderId={orderId} status={currentOrder.status} />
        <OrderStatusHistory
          orderId={orderId}
          channel="manual"
        />
      </div>
      )}

      {/* Edit Items Modal */}
      {showEditItemsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl overflow-hidden shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-[rgba(55,53,47,0.09)] dark:border-gray-800">
              <h3 className="text-lg font-semibold text-[#37352F] dark:text-white">
                Modifica Prodotti
              </h3>
              <button
                onClick={() => setShowEditItemsModal(false)}
                className="p-1 text-[#787774] hover:text-[#37352F] dark:hover:text-white rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 min-h-[400px]">
              <OrderProductSelector
                organizationId={organizationId}
                selectedItems={editItems}
                onAddItem={(item) => setEditItems(prev => [...prev, item])}
                onUpdateItem={(index, updates) => setEditItems(prev => {
                  const next = [...prev];
                  next[index] = { ...next[index], ...updates };
                  return next;
                })}
                onRemoveItem={(index) => setEditItems(prev => prev.filter((_, i) => i !== index))}
              />
            </div>
            <div className="flex gap-3 p-4 border-t border-[rgba(55,53,47,0.09)] dark:border-gray-800">
              <button
                onClick={() => setShowEditItemsModal(false)}
                className="flex-1 px-4 py-2 border border-[rgba(55,53,47,0.16)] dark:border-gray-700 text-[#37352F] dark:text-white rounded hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-800 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleSaveItems}
                disabled={isSavingItems || editItems.length === 0}
                className="flex-1 px-4 py-2 bg-[#37352F] text-white rounded hover:bg-[#2f2d27] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSavingItems && <Loader2 className="w-4 h-4 animate-spin" />}
                Salva Modifiche
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-[#37352F] dark:text-white mb-4">
              Annulla Ordine
            </h3>
            <p className="text-sm text-[#787774] dark:text-gray-400 mb-4">
              Sei sicuro di voler annullare questo ordine? Questa azione rilascerà le prenotazioni stock.
            </p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Motivo dell'annullamento..."
              className="w-full px-3 py-2 border border-[rgba(55,53,47,0.16)] dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-[#37352F] dark:text-white placeholder-[#787774] text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 focus:outline-none mb-4"
              rows={3}
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 px-4 py-2 border border-[rgba(55,53,47,0.16)] dark:border-gray-700 text-[#37352F] dark:text-white rounded hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-800 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleCancelOrder}
                disabled={!cancelReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Conferma Annullamento
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditOrderModal && userId && (
        <ManualOrderCreateModal
          organizationId={organizationId}
          userId={userId}
          editOrderId={currentOrder.id}
          editOrderNumber={currentOrder.order_number}
          editFromDeal={!!currentOrder.deal_id}
          onClose={() => setShowEditOrderModal(false)}
          onSuccess={() => {
            setShowEditOrderModal(false);
            fetchOrderById(orderId, organizationId);
            toast.success('Ordine aggiornato');
            onOrderUpdated?.();
          }}
        />
      )}

      {operationalLabels && (
        <OrderLabelsModal
          isOpen={showLabelsModal}
          onClose={() => setShowLabelsModal(false)}
          orderNumber={currentOrder.order_number}
          items={currentOrder.items || []}
          packCountByOrderItem={processingNotes.packCountByOrderItem}
        />
      )}

      {showSplitModal && currentOrder.deal_id && (
        <DealOrderSplitModal
          dealId={currentOrder.deal_id}
          organizationId={organizationId}
          onClose={() => setShowSplitModal(false)}
          onDone={() => {
            setShowSplitModal(false);
            fetchOrderById(orderId, organizationId);
            onOrderUpdated?.();
          }}
        />
      )}

      <CustomerOrderTemplatePicker
        isOpen={showOrderTemplatePicker}
        title="Stampa / PDF"
        templates={orderTemplates}
        defaultTemplateId={orderDocumentConfig.default_template_id}
        onSelect={t => { setShowOrderTemplatePicker(false); void openCustomerOrderPdf(t.id); }}
        onClose={() => setShowOrderTemplatePicker(false)}
        extraOption={orderDocumentConfig.enabled ? undefined : {
          label: 'Stampa standard',
          onSelect: () => { setShowOrderTemplatePicker(false); printLegacyConfirmation(); },
        }}
      />

      <DdtPrintTemplateModal
        isOpen={showPrintTemplatePicker}
        templates={activePrintTemplates}
        onSelect={printOrder}
        onClose={() => setShowPrintTemplatePicker(false)}
      />
    </div>
  );
}
